import React, { ReactNode } from 'react'
import ContentLoader from 'react-content-loader'
import { BpTooltip } from '../../../cards/tooltip/BpTooltip'

export type TFieldTheme = 'default' | 'focus' | 'disabled' | 'success' | 'error' | 'text'
export type TFieldSize = 'lg' | 'md' | 'sm'

export interface IFieldProps {
  theme?: TFieldTheme
  fieldSize?: TFieldSize
  label?: string
  message?: string
  tooltip?: string | JSX.Element
  info?: string
  children?: ReactNode | undefined
  required?: boolean
  loading?: boolean
  action?: JSX.Element
  rightSection?: JSX.Element | null
}

export const Field = ({
  children,
  theme = 'default',
  fieldSize = 'md',
  label,
  message,
  info,
  tooltip,
  required,
  loading,
  action,
  rightSection,
}: IFieldProps) => {
  const MessageElement = () => {
    const classMessage: string = theme == 'success' ? 'text-success' : 'text-danger'
    return <div className={`text-center text-[12px] leading-[18px] ${classMessage}`}>{message}</div>
  }

  return (
    <div>
      <div className={'flex items-center justify-between'}>
        {label && (
          <div
            className={`mb-[2px] flex items-center gap-[4px] text-[14px] font-medium leading-[24px] ${
              theme == 'disabled' ? 'text-aqua-300' : 'text-focus-500'
            }`}
          >
            {label}
            {required && <span className={'text-default'}> *</span>}
            {tooltip && <BpTooltip title={tooltip} />}
          </div>
        )}
        {action}
      </div>
      <div
        className={`relative rounded-[8px] ${loading && 'overflow-hidden'} ${fieldSize === 'md' && 'p-[2px]'} ${fieldSize === 'lg' && 'p-[4px]'} ${theme === 'default' && 'bg-grey'} ${theme === 'focus' && 'bg-grey-500'} ${theme === 'disabled' && 'bg-grey'} ${theme === 'error' && 'bg-danger-300'} ${theme === 'success' && 'bg-success-300'} ${theme === 'text' && 'bg-transparent'} `}
      >
        {loading && (
          <ContentLoader
            backgroundColor='#f3f3f3'
            className='absolute bottom-0 left-0 top-0 z-10 w-full'
            foregroundColor='#ecebeb'
            speed={2}
          >
            <rect height='100%' rx='8' ry='8' width='100%' x='0' y='0' />
          </ContentLoader>
        )}
        <div
          className={`relative rounded-[6px] shadow-outline ${theme === 'default' && 'bg-white shadow-focus/[0.65]'} ${theme === 'focus' && 'bg-white shadow-default/[0.65]'} ${theme === 'disabled' && 'bg-grey-600 shadow-focus-500/[0.65]'} ${theme === 'error' && 'bg-white shadow-danger/[0.65]'} ${theme === 'success' && 'bg-white shadow-success/[0.65]'} ${theme === 'text' && 'bg-transparent shadow-none'} `}
        >
          <div
            className={`relative flex items-center after:pointer-events-none after:absolute after:inset-0 after:rounded-[6px] after:ring-inset after:ring-default ${!message && theme === 'focus' && 'after:ring-1'} ${!message && theme !== 'text' && 'focus-within:after:ring-1'} `}
          >
            <div className={`w-full ${theme === 'disabled' && 'opacity-60'}`}>{children}</div>
            <div>{rightSection}</div>
          </div>
        </div>
        {message && <MessageElement />}
      </div>
      <p className={'whitespace-pre-line text-[13px] text-aqua-300'}>{info}</p>
    </div>
  )
}
